import { Box, Chip, Stack, styled, Typography } from '@mui/material'
import { Fragment, useMemo } from 'react'

import { formatNumber } from '@/clients/utoniq-core'
import { PreSaleLotteryApplicantDetailFragment } from '@/clients/utoniq-core/schema'
import { useTranslation } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'

type LotteryChoicePriceProps = {
  index: number
  choiceLength?: number
  choiceItemDetail: PreSaleLotteryApplicantDetailFragment['choiceItemDetails'][number]
  winChoiceNumber?: number | null
}

export const LotteryChoicePrice: Organism<LotteryChoicePriceProps> = ({
  index,
  choiceLength = 1,
  choiceItemDetail,
  winChoiceNumber,
}) => {
  const { t } = useTranslation('lotteryApplicationDetail')

  // 合計金額
  const totalPrice = useMemo(
    () =>
      choiceItemDetail.count *
        (choiceItemDetail.skuUnit?.price ?? choiceItemDetail.item?.price ?? 0) +
      (choiceItemDetail.fees?.collections?.reduce(
        (acc, fee) => acc + (fee?.fee ?? 0),
        0
      ) ?? 0) +
      (choiceItemDetail.fees?.konbini?.fee ?? 0) +
      (choiceItemDetail.fees?.shipping?.fee ?? 0),
    [choiceItemDetail]
  )

  // 当選済みの場合、当選した商品以外は表示しない
  if (winChoiceNumber && winChoiceNumber !== index + 1) {
    return null
  }

  return (
    <OrganismFrame>
      {choiceLength > 1 && (
        <Chip
          variant="outlined"
          sx={{
            my: 2,
            typography: 'subtitle2',
            height: '20px',
            lineHeight: 1.8,
            bgcolor: 'background.default',
            display: 'block',
            border: '1px solid',
            borderColor: 'grey.300',
          }}
          size="small"
          label={t('choicePriority', { number: ++index })}
        />
      )}
      <Stack
        sx={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          my: 2,
        }}
      >
        <Typography variant="subtitle1" color="text.secondary">
          {t('totalPrice')}
        </Typography>
        <Typography sx={{ mt: 1 }} variant="body2">
          ¥
          {formatNumber(
            choiceItemDetail.count *
              (choiceItemDetail.skuUnit?.price ??
                choiceItemDetail.item?.price ??
                0)
          )}
        </Typography>
      </Stack>
      <Border sx={{ my: 2 }} />

      {choiceItemDetail.fees?.collections?.map((fee, index) =>
        fee ? (
          <Fragment key={index}>
            <Stack
              sx={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                my: 2,
              }}
            >
              <Typography variant="subtitle1" color="text.secondary">
                {fee?.feeName}
              </Typography>
              <Typography sx={{ mt: 1 }} variant="body2">
                ¥{formatNumber(fee?.fee ?? 0)}
              </Typography>
            </Stack>
            <Border sx={{ my: 2 }} />
          </Fragment>
        ) : null
      )}

      {!!choiceItemDetail.fees.konbini && (
        <Fragment>
          <Stack
            sx={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              my: 2,
            }}
          >
            <Typography variant="subtitle1" color="text.secondary">
              {choiceItemDetail.fees.konbini.feeName}
            </Typography>
            <Typography sx={{ mt: 1 }} variant="body2">
              ¥ {formatNumber(choiceItemDetail.fees.konbini.fee ?? 0)}
            </Typography>
          </Stack>
          <Border sx={{ my: 2 }} />
        </Fragment>
      )}
      {!!choiceItemDetail.fees.shipping && (
        <Fragment>
          <Stack
            sx={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              my: 2,
            }}
          >
            <Typography variant="subtitle1" color="text.secondary">
              {choiceItemDetail.fees.shipping.feeName}
            </Typography>
            <Typography sx={{ mt: 1 }} variant="body2">
              ¥ {formatNumber(choiceItemDetail.fees.shipping.fee ?? 0)}
            </Typography>
          </Stack>
          <Border sx={{ my: 2 }} />
        </Fragment>
      )}

      <Stack
        sx={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          my: 2,
        }}
      >
        <Typography variant="subtitle1" color="text.secondary">
          {t('total')}
        </Typography>
        <Typography sx={{ mt: 1 }} variant="h6">
          ¥ {formatNumber(totalPrice)}
        </Typography>
      </Stack>
    </OrganismFrame>
  )
}

const Border = styled('div')(({ theme }) => ({
  height: '1px',
  backgroundColor: theme.palette.grey['300'],
})) as typeof Box
